/*
 *  This file http-service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ServerErrorHandler } from './error-handler/server-error-handler.service';
import { ConfigurationService } from './configuration.service';
import { SnackbarAlertService } from './snackbar-alert.service';
import { TLoginTokenResponse } from '../models/common.models';

@Injectable()
export class HttpService {
  updateSignalRAuthTokenSubject = new Subject<{ token: TLoginTokenResponse | null, isLogout: boolean }>();
  isOnline = true;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _serverError: ServerErrorHandler,
    public _config: ConfigurationService,
    public _snackBar: SnackbarAlertService
  ) {

  }


  getHeaders(): HttpHeaders {
    const dcStr = localStorage.getItem('domainCode');
    const dc: string = dcStr ?? this._config.domainCode;
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'domainCode': dc,
      'AccessMode': 'W'
    });

  }

  getHeadersIfCaptcha(captcha: string): HttpHeaders {
    const dcStr = localStorage.getItem('domainCode');
    const dc: string = dcStr ?? this._config.domainCode;
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'domainCode': dc,
      'AccessMode': 'W',
      'g-Recaptcha-Response': captcha
    });

  }


  getHeadersUnauthorized(): HttpHeaders {
    const dcStr = localStorage.getItem('domainCode');
    const dc: string = dcStr ?? this._config.domainCode;
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'domainCode': dc,
      'AccessMode': 'W'
    });

  }

  getHeadersForFileUpload(): HttpHeaders {
    const dcStr = localStorage.getItem('domainCode');
    const dc: string = dcStr ?? this._config.domainCode;

    return new HttpHeaders({
      'domainCode': dc,
      'AccessMode': 'W'
    });
  }

  public Post(url:string, param:any, showUnauthorizedError = true): Observable<any> {
    return this._httpClient.post(this._config.base_url + url, param, {
      headers: this.getHeaders(),
    })
      .pipe(
        catchError(err => {
          if (!showUnauthorizedError && err?.status === 401) {
            return throwError(err);
          }
          return this._serverError.handleServerError(err);
        }
        )
      );
  }

  public GetMicroservice(url:string): Observable<any> {
    return this._httpClient.get(this._config.microservice_base_url + url, { headers: this.getHeaders() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }
  public PostMicroservice(url:string, param:any): Observable<any> {
    return this._httpClient.post(this._config.microservice_base_url + url, param, { headers: this.getHeaders() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }
  public GetMicroserviceChat(url:string, isHandleError = true): Observable<any> {
    return this._httpClient.get(this._config.microservice_base_url + url, { headers: this.chatHeader() })
      .pipe(
        catchError((err: any) => {
          if (isHandleError) {
           return this._serverError.handleServerError(err)
          } else {
            return throwError(err);
          }
        })
      );
  }
  public PostMicroserviceChat(url:string, param:any): Observable<any> {
    return this._httpClient.post(this._config.microservice_base_url + url, param, { headers: this.chatHeader() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }
  public GetMicroserviceForDocumentStatusCheck(url:string): Observable<any> {
    return this._httpClient.get(this._config.microservice_base_url + url, { headers: this.chatHeaderForDocStatus() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }
  chatHeaderForDocStatus(): HttpHeaders {
    return new HttpHeaders({});
  }
  chatHeader(): HttpHeaders {
    return new HttpHeaders({
      'isChatHeader': 'true',
    });

  }
  public DeleteMicroservice(url:string): Observable<any> {
    return this._httpClient.delete(this._config.microservice_base_url + url, { headers: this.getHeaders() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }
  public DeleteMicroserviceChat(url:string): Observable<any> {
    return this._httpClient.delete(this._config.microservice_base_url + url, { headers: this.chatHeader() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }
  public PutMicroserviceChat(url:string, param:any): Observable<any> {
    return this._httpClient.put(this._config.microservice_base_url + url, param, { headers: this.chatHeader() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }
  public PostWithoutException(url:string, param:any): Observable<any> {
    return this._httpClient.post(this._config.base_url + url, param, { headers: this.getHeaders() })
      .pipe(
        catchError(err =>
          this.handleServerError(err)
        )
      );
  }

  handleServerError(err:any):Observable<any> {
    return throwError(err);
  }

  public Put(url:string, param:any): Observable<any> {
    return this._httpClient.put(this._config.base_url + url, param, { headers: this.getHeaders() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }

  public Patch(url:string, param:any): Observable<any> {
    return this._httpClient.patch(this._config.base_url + url, param, { headers: this.getHeaders() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }

  PutUnauthorizedMethod(url:string, param:any): Observable<any> {
    const headers = this.getHeadersUnauthorized();
    return this._httpClient.put(this._config.base_url + url, param, { headers: headers })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }
  public PostUnAuthorize(url:string, param:any): Observable<any> {
    const headers = this.getHeadersUnauthorized();
    return this._httpClient.post(this._config.base_url + url, param, { headers: headers })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err, 'isUnauthorize')
        )
      );
  }

  public Get(url: string, showUnauthorizedError = true): Observable<any> {
    return this._httpClient.get(this._config.base_url + url, {
      headers: this.getHeaders(),
    }).pipe(
      catchError(err => {
        if (!showUnauthorizedError && err?.status === 401) {
          return throwError(err);
        }
        return this._serverError.handleServerError(err);
      })
    );
  }

  public GetWithoutHeader(url:string): Observable<any> {
    return this._httpClient.get(this._config.base_url + url)
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }


  public GetLocal(url:string): Observable<any> {
    return this._httpClient.get(url).pipe(catchError(err => this._serverError.handleServerError(err)));
  }

  public Delete(url:string, param?:any): Observable<any> {
    return this._httpClient.delete(this._config.base_url + url, { headers: this.getHeaders() })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }

  public GetUnAuthorize(url:string, param?:any): Observable<any> {
    const headers = this.getHeadersUnauthorized();
    return this._httpClient.get(this._config.base_url + url, { headers: headers })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }

  public PostFile(url:string, param:any): Observable<any> {
    const headers = this.getHeadersForFileUpload();
    headers.delete('Accept');
    return this._httpClient.post(this._config.base_url + url, param,
      { headers: headers })
      .pipe(catchError(err => this._serverError.handleServerError(err)));
  }

  public PostFileUpload(url:string, param:any): Observable<any> {
    const headers = this.getHeadersForFileUpload();
    headers.delete('Accept');
    return this._httpClient.post(this._config.microservice_base_url + url, param,
      { headers: headers })
      .pipe(catchError((err: HttpErrorResponse) => {
        if (err.status === 400 && err.url?.includes('api/File/UploadUserFile')) {
          let allError = '';
          if (err.error?.File) {
            err.error?.File?.forEach((element:string) => {
              allError = element + allError;
            });
          }
          if (err.error?.EmployeeCode) {
            err.error?.EmployeeCode?.forEach((element:string) => {
              allError = element + allError;
            });
          }
          if (err.error?.FolderId) {
            err.error?.FolderId?.forEach((element:string) => {
              allError = element + allError;
            });
          }
          this._snackBar.Alert(allError, '2');
          return throwError(err);
        } else {
         return this._serverError.handleServerError(err);
        }
      }
      ));
  }

  public UploadFile(url:string, param:any): Observable<any> {
    const headers = this.getHeadersForFileUpload();
    headers.delete('Accept');
    return this._httpClient.post(this._config.base_url + url, param,
      { headers: headers, reportProgress: true, observe: 'events' })
      .pipe(catchError(err => this._serverError.handleServerError(err)));
  }


  // For gateway base url api calls
  public PostGateway(url:string, param:any, captcha?: string): Observable<any> {
    const headers = captcha ? new HttpHeaders({ 'g-Recaptcha-Response': captcha }) : new HttpHeaders();
    return this._httpClient.post(this._config.base_auth_url + url, param, { headers: headers })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }

  public GetGateway(url: string): Observable<any> {
    return this._httpClient.get(this._config.base_auth_url + url).pipe(
      catchError(err =>
        this._serverError.handleServerError(err)
      )
    );
  }

  // forgot Password
  public PostUnAuthorizeForForgot(url:string, param:any, isAlertReq = true, captcha?: string): Observable<any> {
    const headers = new HttpHeaders({
      'g-Recaptcha-Response': captcha ?? ''
    });
    return captcha ? this._httpClient.post(this._config.base_url + url, param, { headers }) : this._httpClient.post(this._config.base_url + url, param)
      .pipe(
        catchError(err => {
          if (isAlertReq) {
            this._serverError.handleServerError(err, 'isUnauthorize');
          }
          return throwError(err);
        })
      );
  }




  public stripePost(url:string, data:any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'AccessMode': 'W'
    });
    return this._httpClient.post(this._config.stripe_base_url + url, data,
      { headers: headers })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }


  // ...........stripe Get Api function.................
  public stripeGet(url:string): Observable<any> {
    const headers = new HttpHeaders({
      'AccessMode': 'W'
    });
    return this._httpClient.get(this._config.stripe_base_url + url, { headers: headers })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }


  public stripePut(url:string, param:any): Observable<any> {
    return this._httpClient.put(this._config.stripe_base_url + url, param, {
      headers: new HttpHeaders({
        'AccessMode': 'W'
      })
    })
      .pipe(
        catchError(err =>
          this._serverError.handleServerError(err)
        )
      );
  }

  public getIpAddress(): Observable<object> {
    const base = this._config.base_url.split(':');
    return this._httpClient.get(`${base[0]}://api.ipify.org/?format=json`);
  }

  getLegoHeaders(): HttpHeaders {
    const dcStr = localStorage.getItem('domainCode');
    const dc: string = dcStr ?? this._config.domainCode;
    const legoOrgIdStr = localStorage.getItem('legoOrgId');
    const orgId: string = legoOrgIdStr ?? this._config.legoOrgId;

    if (legoOrgIdStr && this._config.legoOrgId !== legoOrgIdStr) {
      this._config.legoOrgId = legoOrgIdStr;
    }

    return new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'domainCode': dc,
      'AccessMode': 'W',
      'x-org-id': orgId
    });
  }

  // Generic Lego Service Methods
  public GetLegoService(url: string, additionalHeaders?: Record<string, string>): Observable<any> {
    let headers = this.getLegoHeaders();
    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach(key => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }
    return this._httpClient.get(this._config.legoBaseUrl + url, {
      headers,
    }).pipe(catchError(err => this.handleLegoError(err)));
  }

  public PostLegoService(url: string, param: any, additionalHeaders?: Record<string, string>): Observable<any> {
    let headers = this.getLegoHeaders();
    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach(key => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }
    return this._httpClient.post(this._config.legoBaseUrl + url, param, {
      headers,
    }).pipe(catchError(err => this.handleLegoError(err)));
  }

  public PutLegoService(url: string, param: any): Observable<any> {
    return this._httpClient.put(this._config.legoBaseUrl + url, param, {
      headers: this.getLegoHeaders()
    }).pipe(catchError(err => this.handleLegoError(err)));
  }

  public PatchLegoService(url: string, param: any): Observable<any> {
    return this._httpClient.patch(this._config.legoBaseUrl + url, param, {
      headers: this.getLegoHeaders()
    }).pipe(catchError(err => this.handleLegoError(err)));
  }

  public DeleteLegoService(url: string): Observable<any> {
    return this._httpClient.delete(this._config.legoBaseUrl + url, {
      headers: this.getLegoHeaders()
    }).pipe(catchError(err => this.handleLegoError(err)));
  }

  private handleLegoError(err: any): Observable<never> {
    if (err?.status === 401) {
      return throwError(err);
    }
    return this._serverError.handleServerError(err);
  }

}
